import { X, Check } from 'lucide-react';
import { useState } from 'react';

interface Point {
  id: string;
  name: string;
  sensorSerial?: string;
  axisMapping?: {
    X: 'H' | 'V' | 'A';
    Y: 'H' | 'V' | 'A';
    Z: 'H' | 'V' | 'A';
  };
}

interface SensorEditModalProps {
  point: Point;
  onClose: () => void;
  /** @deprecated F-12: no delete endpoint exists — the Delete control was removed. Kept optional for caller compat. */
  onDeleteSensor?: (pointId: string) => void;
  onUpdateSensor: (pointId: string, serialNumber: string, axisMapping: { X: 'H' | 'V' | 'A'; Y: 'H' | 'V' | 'A'; Z: 'H' | 'V' | 'A' }) => void;
}

export function SensorEditModal({ point, onClose, onUpdateSensor }: SensorEditModalProps) {
  const [serialNumber, setSerialNumber] = useState(point.sensorSerial || '');
  const [axisMapping, setAxisMapping] = useState<{ X: 'H' | 'V' | 'A'; Y: 'H' | 'V' | 'A'; Z: 'H' | 'V' | 'A' }>(
    point.axisMapping || { X: 'H', Y: 'V', Z: 'A' }
  );
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Track if changes have been made
  const hasChanges = () => {
    const serialChanged = serialNumber !== (point.sensorSerial || '');
    const axisMappingChanged = 
      axisMapping.X !== (point.axisMapping?.X || 'H') ||
      axisMapping.Y !== (point.axisMapping?.Y || 'V') ||
      axisMapping.Z !== (point.axisMapping?.Z || 'A');
    return serialChanged || axisMappingChanged;
  };

  const handleClose = () => {
    if (hasChanges()) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    if (serialNumber.trim()) {
      onUpdateSensor(point.id, serialNumber.trim(), axisMapping);
      onClose();
    }
  };


  const options: ('H' | 'V' | 'A')[] = ['H', 'V', 'A'];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Only close if clicking the backdrop, not the modal content
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-gray-900">Edit Sensor - {point.name}</h2>
            <p className="text-gray-600 mt-1">Configure sensor serial number and axis orientation</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Serial Number Section */}
          <div className="space-y-3">
            <label htmlFor="serialNumber" className="block text-gray-900">
              Serial Number
            </label>
            <input
              type="text"
              id="serialNumber"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Enter sensor serial number"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Axis Mapping Section */}
          <div className="space-y-4">
            <div>
              <h3 className="text-gray-900 mb-1">Sensor Axis Orientation</h3>
              <p className="text-gray-600">Map sensor axes (X, Y, Z) to analysis orientations (H, V, A)</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* X Axis */}
              <div className="space-y-2">
                <label htmlFor="axisX" className="block text-gray-700">
                  Sensor X equals:
                </label>
                <select
                  id="axisX"
                  value={axisMapping.X}
                  onChange={(e) => setAxisMapping({ ...axisMapping, X: e.target.value as 'H' | 'V' | 'A' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {options.map((option) => (
                    <option key={`X-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Y Axis */}
              <div className="space-y-2">
                <label htmlFor="axisY" className="block text-gray-700">
                  Sensor Y equals:
                </label>
                <select
                  id="axisY"
                  value={axisMapping.Y}
                  onChange={(e) => setAxisMapping({ ...axisMapping, Y: e.target.value as 'H' | 'V' | 'A' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {options.map((option) => (
                    <option key={`Y-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Z Axis */}
              <div className="space-y-2">
                <label htmlFor="axisZ" className="block text-gray-700">
                  Sensor Z equals:
                </label>
                <select
                  id="axisZ"
                  value={axisMapping.Z}
                  onChange={(e) => setAxisMapping({ ...axisMapping, Z: e.target.value as 'H' | 'V' | 'A' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {options.map((option) => (
                    <option key={`Z-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Axis Mapping Visual */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-700 mb-2">Current Mapping:</p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-gray-900">
                <span className="px-3 py-1 bg-white border border-gray-300 rounded">
                  X → {axisMapping.X}
                </span>
                <span className="px-3 py-1 bg-white border border-gray-300 rounded">
                  Y → {axisMapping.Y}
                </span>
                <span className="px-3 py-1 bg-white border border-gray-300 rounded">
                  Z → {axisMapping.Z}
                </span>
              </div>
            </div>

            {/* Legend */}
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="text-gray-900">H</span> = Horizontal</p>
              <p><span className="text-gray-900">V</span> = Vertical</p>
              <p><span className="text-gray-900">A</span> = Axial</p>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-lg transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!serialNumber.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>

      {/* Close Confirmation Dialog */}
      {showCloseConfirm && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-gray-900 mb-4">Unsaved Changes</h3>
            <p className="text-gray-600 mb-6">
              You have unsaved changes. Are you sure you want to close without saving?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-lg transition-all"
              >
                Keep Editing
              </button>
              <button
                onClick={() => {
                  setShowCloseConfirm(false);
                  onClose();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
              >
                Close Without Saving
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}